import { serviceFootageOtherFieldValues } from '@borradh-workspace/labels';
/**
 * catalog response PROJECTIONS — hand-composed from generated atoms.
 *
 * Covers the catalog response surface: services, service-categories, offers /
 * promotions, membership plans + sold memberships, service packages, and the
 * inventory-adjacent product brand / category / supplier lookups. Composed with
 * `z.object` / `.extend` / `z.array` from the generated atoms; pure Zod.
 *
 * Follows the proof pattern in ./leads.ts. Each response exports a schema plus
 * its `z.infer` type. Names are chosen not to collide with sibling response
 * files or the re-exported `*AtomSchema` names.
 */
import { z } from 'zod';
import { leadMembershipAtomSchema, membershipPlanAtomSchema, offerAtomSchema, organizationPackageAtomSchema, organizationPackageItemAtomSchema, organizationServiceAtomSchema, organizationServiceCategoryAtomSchema, organizationServiceVariantAtomSchema, productBrandAtomSchema, productCategoryAtomSchema, supplierAtomSchema, } from '../generated/index.js';
import { serviceTreatmentSelectionSchema } from '../requests/service-footage.js';
/** One row a spreadsheet import could not write, or a warning about one it did. */
export const importRowErrorSchema = z.object({
    row: z.number(),
    message: z.string(),
    data: z.record(z.string(), z.unknown()).optional(),
});
/** The counters every spreadsheet import reports; each endpoint extends it. */
export const importSummaryBaseSchema = z.object({
    imported: z.number(),
    skipped: z.number(),
    updated: z.number(),
    errors: z.array(importRowErrorSchema),
    /** Data rows in the file, excluding the header. */
    rowsInFile: z.number(),
    /** Rows that survived parsing and had a usable key. */
    cleanedRows: z.number(),
    /** Rows dropped for having no usable key. */
    skippedRows: z.number(),
    /** Header → field mapping the import used. */
    columnMapping: z.record(z.string(), z.string()),
});
// ============================================================================
// Shared pricing building blocks
// ============================================================================
/**
 * The org's single display currency ({ code, symbol }) — a computed shape (no
 * backing table), resolved from the org's country via `currencyForCountry`.
 * Threaded into the venue config and booking config so the frontend can render
 * prices via `formatServicePrice` without guessing the symbol.
 */
export const currencySchema = z.object({
    code: z.string(),
    symbol: z.string(),
});
/**
 * A service variant (customer-chosen pricing option) on the wire — the
 * `organization_service_variant` atom, verbatim. Backs the variant CRUD
 * endpoints and the `variants` array of the listed-service projection.
 */
export const serviceVariantSchema = organizationServiceVariantAtomSchema;
/** `GET /organization-services/:id/variants` — `{ items }` (a bare list). */
export const listServiceVariantsResponseSchema = z.object({
    items: z.array(serviceVariantSchema),
});
// ============================================================================
// SERVICES
// ============================================================================
/**
 * An organization service on the wire.
 *
 * The atom types `painPoints` / `expectedResults` as `z.unknown()` (the
 * generator can't see through `jsonb().$type<...>()`), so they are hand-modeled
 * here as `string[] | null` to match the DB column shape. `priceText` stays a
 * free-text string (not integer cents) — services store their price as
 * display text, not a money amount.
 */
export const organizationServiceSchema = organizationServiceAtomSchema.extend({
    footageTreatments: z.array(serviceTreatmentSelectionSchema).default([]),
    footageSuggestions: z.array(serviceTreatmentSelectionSchema).default([]),
    footageOtherFields: z
        .array(z.enum(serviceFootageOtherFieldValues))
        .default([]),
    painPoints: z.array(z.string()).nullable(),
    expectedResults: z.array(z.string()).nullable(),
});
/**
 * A listed service plus the two media-gating booleans the list endpoint
 * computes: `hasGraphicMedia` (uploaded photos/screenshots usable for a
 * graphic) and `hasVideoFootage` (its own uploaded video clips). Neither is a
 * column — both are joined/computed at list time.
 */
export const listedServiceSchema = organizationServiceSchema.extend({
    hasGraphicMedia: z.boolean(),
    hasVideoFootage: z.boolean(),
    // All variants (active + inactive), by sortOrder — so the dashboard service
    // form can load and edit them. Empty for single-price services.
    variants: z.array(serviceVariantSchema),
    // The branches offering the service. EMPTY MEANS EVERYWHERE (the empty-
    // junction convention), not "offered nowhere" — the promotion editor groups
    // its service picker by branch and needs the links.
    locationIds: z.array(z.string()),
});
/**
 * `POST /organization-services/:id/locations` — the branches offering the
 * service AFTER the add.
 *
 * The full resulting set rather than the delta, so a caller never has to
 * reconstruct it. An EMPTY array here keeps its usual meaning — offered at
 * every branch — which is what a no-op add against an unassigned service
 * returns.
 */
export const addServiceLocationsResponseSchema = z.object({
    serviceId: z.string(),
    locationIds: z.array(z.string()),
});
/** `GET /organization-services` — the `{ items, total, limit, offset }` wrapper. */
export const listServicesResponseSchema = z.object({
    items: z.array(listedServiceSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
});
// ============================================================================
// SERVICE CATEGORIES
// ============================================================================
/** A per-org service category row (the atom, verbatim). */
export const organizationServiceCategorySchema = organizationServiceCategoryAtomSchema;
/** `GET /service-categories` — a bare array of category rows (no wrapper). */
export const listServiceCategoriesResponseSchema = z.array(organizationServiceCategorySchema);
// ============================================================================
// OFFERS / PROMOTIONS
// ============================================================================
/**
 * An offer on the wire. Money is integer cents (`discountAmountCents`,
 * `offerPriceCents`) and `discountPercent` is a whole number; which of those is
 * populated depends on `discountType` (percentage vs fixed-amount vs
 * buy-X-get-Y) — the atom models every field nullable rather than as a
 * discriminated union, mirroring the flat DB row.
 *
 * NO `originalPriceCents`: an offer has no price of its own to discount from.
 * The legacy column is unread (dropped in a later migration); the "was" price
 * is the linked service's — see `basePriceCents` on the linked shapes below.
 */
export const offerSchema = offerAtomSchema.omit({
    unreadLegacyOriginalPriceCents: true,
});
/**
 * The price this offer discounts FROM, read from its ONE linked service (with
 * the branch's override when the request names a branch). Null when it links
 * none or several, or the service is POA / free — there is then no single
 * honest "was" price (shared/promotion-pricing in features).
 */
const offerBasePriceCents = z.number().int().nullable();
/**
 * An offer joined with the ids of the services + locations it is scoped to.
 * `locationIds === []` means the offer applies to every org location
 * (empty-junction convention). This is the flat list-item shape.
 */
export const offerWithLinksSchema = offerSchema.extend({
    serviceIds: z.array(z.string()),
    packageIds: z.array(z.string()),
    locationIds: z.array(z.string()),
    basePriceCents: offerBasePriceCents,
});
/** `GET /offers` — the `{ items, total, limit, offset }` wrapper. */
export const listOffersResponseSchema = z.object({
    items: z.array(offerWithLinksSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
});
/**
 * `GET /offers/:id` — the detail projection, which nests the offer under an
 * `offer` key alongside its linked service + location ids.
 */
export const offerWithServicesSchema = z.object({
    offer: offerSchema,
    serviceIds: z.array(z.string()),
    packageIds: z.array(z.string()),
    locationIds: z.array(z.string()),
    basePriceCents: offerBasePriceCents,
});
/** `POST /offers/import-csv` — the import outcome summary. */
export const importOffersCsvResponseSchema = importSummaryBaseSchema.extend({
    /** Service names in the file that matched a catalogue service. */
    servicesLinked: z.number(),
    /** Location names in the file that matched one of the org's branches. */
    locationsLinked: z.number(),
});
// ============================================================================
// MEMBERSHIPS
// ============================================================================
/** A membership plan on the wire (`priceCents` is integer cents). */
export const membershipPlanSchema = membershipPlanAtomSchema;
/** A membership plan plus the ids of the services it covers. */
export const membershipPlanWithServicesSchema = membershipPlanSchema.extend({
    serviceIds: z.array(z.string()),
});
/**
 * A listed plan: the plan, the services it covers, and the branches selling it.
 *
 * `locationIds` is on the LIST only, mirroring `listedServiceSchema` — the
 * import dialog needs to tell "already sold here" from "available to copy", and
 * the join table is exposed nowhere else. EMPTY MEANS EVERY BRANCH.
 */
export const listedMembershipPlanSchema = membershipPlanWithServicesSchema.extend({
    locationIds: z.array(z.string()),
});
/** `GET /membership-plans` — a bare array of plans (no wrapper). */
export const listMembershipPlansResponseSchema = z.array(listedMembershipPlanSchema);
/** `POST /membership-plans/import-csv` — the import outcome summary. */
export const importMembershipPlansCsvResponseSchema = importSummaryBaseSchema.extend({
    /** Service names in the file that matched a catalogue service. */
    servicesLinked: z.number(),
});
/** A sold membership (a plan bought by a lead). */
export const leadMembershipSchema = leadMembershipAtomSchema;
/** A sold membership joined with the plan it was bought from. */
export const leadMembershipWithPlanSchema = leadMembershipSchema.extend({
    plan: membershipPlanSchema,
    lead: z
        .object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string().nullable(),
    })
        .nullable()
        .optional(),
});
/** `GET /lead-memberships` — a bare array of sold memberships (no wrapper). */
export const listLeadMembershipsResponseSchema = z.array(leadMembershipWithPlanSchema);
// ============================================================================
// PACKAGES
// ============================================================================
/** A service package on the wire (`priceCents` is integer cents). */
export const organizationPackageSchema = organizationPackageAtomSchema;
/** A raw package line item (the atom, verbatim). */
export const organizationPackageItemSchema = organizationPackageItemAtomSchema;
/** A package line item joined with the full service it references. */
export const packageItemWithServiceSchema = organizationPackageItemSchema.extend({
    service: organizationServiceSchema,
});
/** `GET /packages/:id` — a package with its expanded, service-joined items. */
export const packageWithItemsSchema = organizationPackageSchema.extend({
    items: z.array(packageItemWithServiceSchema),
    /** Branches the package is sold at; empty means every branch. */
    locationIds: z.array(z.string()),
});
// ============================================================================
// INVENTORY LOOKUPS (product brand / category / supplier)
// ============================================================================
/** A product brand row (the atom, verbatim). */
export const productBrandSchema = productBrandAtomSchema;
/** `GET /product-brands` — a bare array of brands (no wrapper). */
export const listProductBrandsResponseSchema = z.array(productBrandSchema);
/** A product category row (the atom, verbatim — name only, no description). */
export const productCategorySchema = productCategoryAtomSchema;
/** `GET /product-categories` — a bare array of categories (no wrapper). */
export const listProductCategoriesResponseSchema = z.array(productCategorySchema);
/** A supplier row (the atom, verbatim). */
export const supplierSchema = supplierAtomSchema;
/** `GET /suppliers` — a bare array of suppliers (no wrapper). */
export const listSuppliersResponseSchema = z.array(supplierSchema);
// ============================================================================
// Catalogue import — a computed result summary (no backing table).
// ============================================================================
/** A single rejected row from a catalogue import. */
export const serviceImportErrorSchema = z.object({
    row: z.number(),
    message: z.string(),
    data: z.record(z.string(), z.unknown()).optional(),
});
/**
 * `POST /organization-services/import-csv` — the spreadsheet import summary.
 *
 * Named `…Response` throughout to avoid colliding with the feature package's
 * `ImportServicesCsvSummary`, which is the same shape on the server side.
 */
export const importServicesCsvResponseSchema = z.object({
    imported: z.number(),
    skipped: z.number(),
    updated: z.number(),
    errors: z.array(serviceImportErrorSchema),
    /** Data rows in the file, excluding the header. */
    rowsInFile: z.number(),
    /** Rows that survived parsing and had a usable name. */
    cleanedRows: z.number(),
    /** Rows dropped for having no service name. */
    skippedRows: z.number(),
    /** Categories created because the file named them and the org had none. */
    categoriesCreated: z.number(),
    /** Options created under a parent service, from rows with a `parent` column. */
    variantsCreated: z.number(),
    /** New team-member links added from a staff column. */
    staffAssigned: z.number(),
    /** Header → service-field mapping the import used. */
    columnMapping: z.record(z.string(), z.string()),
});
// ============================================================================
// RESOURCES (rooms & equipment)
// ============================================================================
/** `POST /resources/import-csv` — the import outcome summary. */
export const importResourcesCsvResponseSchema = importSummaryBaseSchema.extend({
    /** Categories created because the file named them and the org had none. */
    categoriesCreated: z.number(),
});
//# sourceMappingURL=catalog.js.map